import { useCallback, useMemo } from "react";
import {
  useHotelRoomRate as useHotelRoomRateBase,
  type HotelRoomRateProps,
} from "@b2bportal/core-hotels";
import {
  CustomDateTimeFormatStyles,
  ensureExhaustive,
} from "@b2bportal/core-utilities";
import {
  type CancellationPolicyV2,
  CancellationPolicyV2Enum,
  EffectiveRangeEnum,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";

export const useHotelRoomRate = (props: HotelRoomRateProps) => {
  const { product } = props;
  const { t, formatDateTime } = useI18nContext();

  const result = useHotelRoomRateBase(props);

  const getRefundableUntilDate = useCallback(
    (cancellationPolicy: CancellationPolicyV2) => {
      if (
        cancellationPolicy.CancellationPolicyV2 !==
        CancellationPolicyV2Enum.FullyRefundable
      ) {
        return null;
      }

      if (
        cancellationPolicy.effectiveRange.EffectiveRange !==
        EffectiveRangeEnum.Available
      ) {
        return null;
      }

      const { startTime } = cancellationPolicy.effectiveRange;
      return formatDateTime(
        new Date(removeTimezone(startTime)),
        CustomDateTimeFormatStyles.ShortMonthDayDate
      );
    },
    [formatDateTime]
  );

  const rateText = useMemo(() => {
    if (product.cancellationPolicyV2 == null) {
      return {
        title: "", //TODO
      };
    }

    switch (product.cancellationPolicyV2.CancellationPolicyV2) {
      case CancellationPolicyV2Enum.FullyRefundable: {
        const untilDate = getRefundableUntilDate(product.cancellationPolicyV2);
        return {
          title:
            untilDate != null
              ? t("core-hotels.roomSelection.refundable", {
                  untilDate,
                })
              : product.cancellationPolicyV2?.primaryText,
          // Full provider cancellation policy, surfaced behind "See more details".
          fullPolicyText: product.cancellationPolicyV2?.secondaryText,
        };
      }
      case CancellationPolicyV2Enum.NonRefundable:
      case CancellationPolicyV2Enum.PartiallyRefundable:
        return {
          title: product.cancellationPolicyV2?.primaryText,
          fullPolicyText: product.cancellationPolicyV2?.secondaryText,
        };
      case CancellationPolicyV2Enum.Unknown:
        return {
          title: product.cancellationPolicyV2?.primaryText,
        };

      default:
        return ensureExhaustive(product.cancellationPolicyV2, {
          title: "",
        });
    }
  }, [getRefundableUntilDate, product.cancellationPolicyV2, t]);

  return { ...result, text: rateText };
};
